import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Body2, Dot } from "@activecollab/components";
import classNames from "classnames";
import { List } from "immutable";
import PropTypes from "prop-types";
import ElementPlaceholder from "~/components/ElementPlaceholder/ElementPlaceholder";
import { EntityLink } from "~/components/EntityLink";
import { useDraggableContext } from "~/components/ListView/context/DraggableContext";
import { ListItem } from "~/components/ListView/ListItem";
import { TaskItemColumn } from "~/components/ListView/TaskItemColumn";
import { MultiAssigneesPicker } from "~/components/MultiAssigneesPicker/MultiAssigneesPicker";
import { useMyTasksColumnsContext } from "~/components/MyWork/MyTasks/context";
import ProjectName from "~/components/project/ProjectName";
import { ProjectMembersAssigneePicker } from "~/components/ProjectMembersAssigneePicker";
import { TaskSignifiers } from "~/components/task-signifiers";
import TaskCompleteCheckbox from "~/components/tasks/TaskCompleteCheckbox";
import TaskFormSubmitContainer from "~/components/tasks/TaskFormSubmitContainer";
import { TaskOptionsContainer, TaskOptionsMenu } from "~/components/tasks/TaskOptions/index.jsx";
import StopwatchSwitch from "~/components/Timer/StopwatchSwitch";
import { showErrorToast, showSuccessToast } from "~/components/Toast";
import { EDIT_TASK_ERROR, EDIT_TASK_SUCCESS } from "~/store/action_types";
import { editTask, loadTask } from "~/store/actions/actions.tasks";
import { getConfigOption, getIsMultipleAssigneesEnabled } from "~/store/selectors/selector.configOptions";
import { getProjectById } from "~/store/selectors/selector.projects";
import {
  makeGetOpenParentDependenciesCountByIdSelector,
  getTaskHashUrl,
  getTaskViewUrl,
  getDefaultTaskById,
  getProjectIdByTaskId,
} from "~/store/selectors/selector.tasks";
import {
  hasCollaborativePermissionsOnProject,
  hasLoggedUserElevatedPermissions,
} from "~/store/selectors/selector.users";

export const UserWorkTaskItem = React.memo(
  ({ id, className, showProjectName, userId, showAssignee, hideStopwatch }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const projectId = useSelector((state) => getProjectIdByTaskId(state, id));
    const project = useSelector((state) => getProjectById(state, projectId));
    const [loadingTask, setLoadingTask] = useState(false);
    const [editId, setEditId] = useState(0);
    const { notifyFormOpen, notifyFormClosed } = useDraggableContext();
    const task = useSelector((state) => getDefaultTaskById(state, id));
    const assigneeId = task?.get("assignee_id");
    const assigneeIds = useMemo(() => {
      if (task && task.assignee_ids) {
        if (List.isList(task.assignee_ids)) {
          return task.assignee_ids.toArray();
        }
        if (Array.isArray(task.assignee_ids)) {
          return task.assignee_ids;
        }
      }
      return [];
    }, [task]);
    const { columns, task_signifiers } = useMyTasksColumnsContext();
    const { ref, inView } = useInView();
    const data = useOutletContext();
    const team = useMemo(() => (data ? data.team : {}), [data]);
    const columnsToRender = useMemo(
      () =>
        columns.filter((c) => {
          if (showAssignee) {
            return c.active;
          }
          return c.value && c.active;
        }),
      [columns, showAssignee]
    );

    const viewUrl = useSelector((state) => getTaskViewUrl(state, id));
    const hashUrl = useSelector((state) => getTaskHashUrl(state, id));

    const defaultCompleted = task.get("is_completed");
    const getOpenParentDependenciesCountById = useMemo(makeGetOpenParentDependenciesCountByIdSelector, []);
    const parentDependenciesCount = useSelector((state) => getOpenParentDependenciesCountById(state, id));
    const showTaskNumber = useSelector((state) => getConfigOption(state, `show_task_id`));
    const multipleAssigneesEnabled = useSelector(getIsMultipleAssigneesEnabled);

    const shouldShowStopwatch = useMemo(() => {
      if (userId || showAssignee || hideStopwatch) {
        return false;
      }

      const column = columns.find((c) => {
        return c.name === "Time";
      });

      return column?.active && project?.is_tracking_enabled;
    }, [columns, hideStopwatch, project?.is_tracking_enabled, showAssignee, userId]);

    const handleClickLink = (e) => e.stopPropagation();

    const getSignifier = useCallback(
      (name) => {
        const signifierMap = task_signifiers.reduce((prev, next) => {
          prev[next.name] = next.value;
          return prev;
        }, {});
        switch (name) {
          case "subtasks":
            return signifierMap["subtasks"] ? task.open_subtasks : 0;
          case "dependencies": {
            const { parents_count = 0, children_count = 0 } = task.open_dependencies?.toJS() || {};
            const dependencies = parents_count + children_count || 0;
            return signifierMap["dependencies"] ? dependencies : 0;
          }
          case "client_visibility":
          case "hidden_from_client": {
            return signifierMap["client_visibility"];
          }
          case "comments":
            return signifierMap["comments"] ? task.comments_count : 0;
          case "recurrence":
          case "recurring": {
            return signifierMap["recurrence"] && Boolean(task.created_from_recurring_task_id > 0);
          }
          default:
            return 0;
        }
      },
      [
        task.comments_count,
        task.created_from_recurring_task_id,
        task.open_dependencies,
        task.open_subtasks,
        task_signifiers,
      ]
    );

    const handleChangeAssignee = useCallback(
      async (assigneeId) => {
        const { type } = await dispatch(editTask(projectId, id, { assignee_id: assigneeId }));
        if (type === EDIT_TASK_SUCCESS && !team.members.includes(assigneeId)) {
          showSuccessToast(`${t("Task reassigned")}`);
        }

        if (type === EDIT_TASK_ERROR) {
          showErrorToast(`${t("Failed to update task")}.`);
        }
      },
      [dispatch, projectId, id, team, t]
    );

    const handleChangeAssignees = useCallback(
      async (ids) => {
        const { type } = await dispatch(editTask(projectId, id, { assignee_ids: ids }));

        let isInTeam = false;
        if (ids.some((id) => team.members.includes(id))) {
          isInTeam = true;
        }
        if (type === EDIT_TASK_SUCCESS && !isInTeam) {
          showSuccessToast(`${t("Task reassigned")}`);
        }

        if (type === EDIT_TASK_ERROR) {
          showErrorToast(`${t("Failed to update task")}.`);
        }
      },
      [dispatch, projectId, id, team, t]
    );

    const canEdit = useSelector(hasLoggedUserElevatedPermissions);
    const canCollaborateOnProject = useSelector((state) => hasCollaborativePermissionsOnProject(state, projectId));

    const handleStartEdit = useCallback(() => {
      setLoadingTask(true);

      Promise.all([dispatch(loadTask(projectId, id, true))])
        .then(() => {
          setLoadingTask(false);
          setEditId(id);
          notifyFormOpen(id);
        })
        .catch(() => setLoadingTask(false));
    }, [dispatch, projectId, id, notifyFormOpen]);

    const handleResetEdit = useCallback(() => {
      setLoadingTask(false);
      setEditId(0);
      notifyFormClosed(id);
    }, [notifyFormClosed, id]);

    const handleClick = useCallback(() => {
      navigate(`?modal=${`Task-${id}-${task?.get("project_id")}`}`);
    }, [id, navigate, task]);

    const showTreeDot = canCollaborateOnProject && !defaultCompleted;

    return (
      <ListItem
        className={classNames(
          "task-list-item hover:tw-opacity-100 tw-flex-1 tw-ml-0 tw-border-b-[1px] tw-border-theme-300 tw-items-stretch",
          {
            "tw-opacity-50": defaultCompleted,
            "tw-h-auto tw-p-4 tw-border-none hover:tw-bg-inherit": editId === id,
            "tw-pl-1": editId !== id,
            "tw-flex tw-justify-between tw-w-full tw-h-full tw-cursor-pointer": !loadingTask && editId !== id,
            "tw-max-h-[550px] tw-min-h-auto tw-self-stretch": !loadingTask && editId === id,
          },
          className
        )}
        onClick={!loadingTask && editId !== id ? handleClick : undefined}
        data-testid="task-item-navigate"
        ref={ref}
      >
        {!loadingTask && editId === id ? (
          <TaskFormSubmitContainer
            id={id}
            taskListId={task?.get("task_list_id")}
            projectId={projectId}
            submitButtonLabel={t("Save")}
            onCancel={handleResetEdit}
            onSubmit={handleResetEdit}
            shouldOverflow
          />
        ) : (
          <>
            <div className="tw-flex tw-items-center tw-w-full tw-min-w-0 tw-ml-5">
              <TaskCompleteCheckbox
                taskId={id}
                defaultCompleted={defaultCompleted}
                parentDependenciesCount={parentDependenciesCount}
                openSubtasksCount={task.get("open_subtasks")}
                disabled={!canEdit}
                className={classNames("task-complete-checkbox tw-shrink-0 tw-mr-2", {
                  "tw-mr-3": showTaskNumber,
                })}
              />
              <div className="tw-overflow-hidden tw-flex tw-flex-1 tw-gap-1 tw-mr-1 tw-items-center">
                <Body2
                  as={EntityLink}
                  to={`/${viewUrl}`}
                  hashUrl={hashUrl}
                  weight="medium"
                  onClick={handleClickLink}
                  className={classNames("tw-truncate", {
                    "tw-text-red-alert": task?.get("is_important"),
                  })}
                >
                  {showTaskNumber && task?.get("task_number") ? `#${task?.get("task_number")}: ` : null}
                  {task?.get("name")}
                </Body2>
                {showProjectName ? (
                  <>
                    <Dot
                      className="tw-mx-2 tw-shrink-0"
                      color="var(--color-theme-500)"
                      size={4}
                    />
                    <Body2
                      color="tertiary"
                      className="tw-truncate"
                    >
                      <ProjectName id={projectId} />
                    </Body2>
                  </>
                ) : null}
                <TaskSignifiers
                  subtasks={getSignifier("subtasks")}
                  hiddenFromClients={getSignifier("client_visibility")}
                  taskHiddenFromClient={task.is_hidden_from_clients}
                  fromRecurring={getSignifier("recurrence")}
                  comments={getSignifier("comments")}
                  dependencies={getSignifier("dependencies")}
                  className="tw-hidden md:tw-flex"
                />
              </div>
              {shouldShowStopwatch && inView ? (
                <StopwatchSwitch
                  parentType="Task"
                  parentId={id}
                  className="tw-shrink-0 tw-hidden lg:tw-flex tw-ml-3"
                />
              ) : (
                <ElementPlaceholder className="tw-h-6 tw-w-6 tw-shrink-0 tw-hidden lg:tw-flex lg:tw-invisible" />
              )}
              {showAssignee && inView ? (
                multipleAssigneesEnabled ? (
                  <MultiAssigneesPicker
                    projectId={projectId}
                    assigneeIds={assigneeIds}
                    onChange={handleChangeAssignees}
                    hiddenFromClient={task.is_hidden_from_clients}
                    className="tw-mr-1"
                    canEdit
                  />
                ) : (
                  <ProjectMembersAssigneePicker
                    projectId={projectId}
                    assigneeId={assigneeId}
                    fakeAssigneeName={task.get("fake_assignee_name")}
                    onChange={handleChangeAssignee}
                    hiddenFromClient={task.is_hidden_from_clients}
                    className={classNames({
                      "show-on-hover": !assigneeId,
                    })}
                    position="bottom-end"
                  />
                )
              ) : null}
            </div>
            {columnsToRender.map(({ value, name }, index) => {
              return (
                <TaskItemColumn
                  type={name}
                  value={!showAssignee ? value : true}
                  name={name.toLowerCase()}
                  taskId={id}
                  projectId={projectId}
                  key={index}
                  inView={inView}
                  isForMyWork
                  isEmpty={name === "Time" && !project?.is_tracking_enabled}
                />
              );
            })}
            {showTreeDot ? (
              <TaskOptionsMenu
                targetSize="medium"
                onStartEdit={handleStartEdit}
                className="tw-flex tw-items-center tw-shrink-0 options-target-menu target-menu options-target-menu-show tw-mr-1 tw-self-center"
                position="bottom-end"
              >
                <TaskOptionsContainer
                  id={id}
                  projectId={projectId}
                />
              </TaskOptionsMenu>
            ) : canEdit ? (
              <div className="tw-flex tw-items-center tw-shrink-0 tw-w-9" />
            ) : null}
          </>
        )}
      </ListItem>
    );
  }
);

UserWorkTaskItem.propTypes = {
  id: PropTypes.oneOfType([
    PropTypes.string, // string is used as placeholder ID until backend returns the real ID
    PropTypes.number,
  ]).isRequired,
  className: PropTypes.string,
  userId: PropTypes.number,
  inView: PropTypes.bool,
  showProjectName: PropTypes.bool,
  showAssignee: PropTypes.bool,
  hideStopwatch: PropTypes.bool,
};

UserWorkTaskItem.defaultProps = {
  showAssignee: false,
  hideStopwatch: false,
};

UserWorkTaskItem.displayName = "UserWorkTaskItem";
